import { Component, inject, TemplateRef, ViewChild } from '@angular/core';
import { LoggerService } from '@bequeathed/ngx-app-core';
import { MessageService } from '@bequeathed/ngx-services';
import { PageUtil } from '@bequeathed/ngx-utils';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

const LOG: LoggerService = LoggerService.get('EditConfirmationDialogComponent');

/**
 * Pre-edit confirmation dialog for mid-interview backward navigation.
 *
 * Shown when the user activates a return control on an eligible (completed,
 * non-immutable, non-current) topic, before the edit flow is entered. The copy
 * warns that answers given after that point *may* need to be asked again
 * (Requirement 2.1, 2.4), reflecting that still-valid downstream answers are
 * preserved server-side.
 *
 * Reuses the NgBootstrap modal pattern from `EditQuestionPopupComponent` /
 * `ConfirmPopupComponent`: an inline `#modal` `ng-template` opened via
 * `NgbModal.open(...)`, with the user's choice surfaced through the modal
 * result promise.
 *
 * Accessibility (Requirement 7.6):
 * - On open, this component explicitly moves keyboard focus into the dialog by
 *   focusing the title heading via `PageUtil.setFocus('edit-confirm-title')`
 *   (the heading carries `tabindex="-1"` so it can receive programmatic focus).
 *   This mirrors {@link ChatInterfaceComponent.focusMain}. Without this,
 *   `NgbModal` does not move focus on open (there is no autofocus target); it
 *   only traps focus *once something inside is focused*. Moving focus to the
 *   title ensures keyboard navigation works, the NgbModal focus-trap engages,
 *   and screen readers announce the dialog.
 * - `NgbModal` closes the dialog on Escape by default (`keyboard: true`). Escape
 *   rejects the result promise, which this component treats as a dismissal.
 * - The dialog has an accessible name via `aria-labelledby` pointing at the
 *   title, plus `role="dialog"` / `aria-modal="true"` supplied by NgbModal.
 * - On dismiss (Escape, backdrop, or the dismiss button) focus is returned to
 *   the control that opened the dialog when the caller supplies it (see
 *   {@link ConfirmationConfig.originatingElement}). Confirm intentionally does
 *   not restore focus here, because the caller (task 8) moves focus to the
 *   loaded question heading instead.
 *
 * This dialog is not yet wired to the navigator; wiring happens in task 8.
 *
 * @author Kiro
 */
@Component({
    selector: 'edit-confirmation-dialog',
    standalone: true,
    imports: [],
    templateUrl: './edit-confirmation-dialog.component.html',
    styleUrl: './edit-confirmation-dialog.component.sass'
})
export class EditConfirmationDialogComponent {

    /** Message keys for all copy. Sourced from `WILL.interview.*` (no hard-coded strings). */
    static readonly TITLE_KEY = 'WILL.interview.edit-confirm.title';
    static readonly BODY_KEY = 'WILL.interview.edit-confirm.body';
    static readonly CONFIRM_KEY = 'WILL.interview.edit-confirm.confirm';
    static readonly DISMISS_KEY = 'WILL.interview.edit-confirm.dismiss';

    // @ts-expect-error Provided from the template
    @ViewChild('modal') private modalContent: TemplateRef<EditConfirmationDialogComponent>;

    public messages: MessageService = inject(MessageService);
    private modalService: NgbModal = inject(NgbModal);

    // @ts-expect-error Assigned in open(...) before the modal renders
    private modalRef: NgbModalRef;

    /** The element to return focus to when the dialog is dismissed. */
    private originatingElement: HTMLElement | undefined;

    /** Dialog title copy. */
    get title(): string {
        return this.messages.getText(EditConfirmationDialogComponent.TITLE_KEY);
    }

    /** Body copy explaining later answers *may* be re-asked. */
    get body(): string {
        return this.messages.getText(EditConfirmationDialogComponent.BODY_KEY);
    }

    /** Confirm button label. */
    get confirmLabel(): string {
        return this.messages.getText(EditConfirmationDialogComponent.CONFIRM_KEY);
    }

    /** Dismiss button label. */
    get dismissLabel(): string {
        return this.messages.getText(EditConfirmationDialogComponent.DISMISS_KEY);
    }

    /**
     * Open the confirmation dialog.
     *
     * @param config optional configuration; supply `originatingElement` so focus
     *               can be restored to it when the dialog is dismissed.
     * @returns a promise resolving to `true` when the user confirms and `false`
     *          when the user dismisses (via the dismiss button, Escape, or the
     *          backdrop). The promise never rejects.
     */
    open(config?: ConfirmationConfig): Promise<boolean> {
        this.originatingElement = config?.originatingElement;

        this.modalRef = this.modalService.open(this.modalContent, {
            centered: true,
            windowClass: 'popup-window',
            ariaLabelledBy: 'edit-confirm-title'
        });

        // Move keyboard focus into the dialog (the title heading). NgbModal does
        // not move focus on open here — it only traps focus once something inside
        // is focused — so mirror ChatInterfaceComponent.focusMain() and focus the
        // title after the DOM renders (PageUtil.setFocus applies a ~50ms delay).
        PageUtil.setFocus('edit-confirm-title');

        return new Promise<boolean>(resolve => {
            this.modalRef.result.then(
                (confirmed: boolean) => resolve(confirmed === true),
                () => {
                    // Rejection covers Escape, backdrop click, and the dismiss button.
                    this.restoreFocus();
                    resolve(false);
                }
            );
        });
    }

    /** Confirm the edit. Resolves the result promise with `true`. */
    confirm(): void {
        LOG.debug('confirm', 'User confirmed the pre-edit warning');
        this.modalRef.close(true);
    }

    /**
     * Dismiss the dialog without changing interview state. Resolves the result
     * promise with `false` and returns focus to the originating control.
     */
    dismiss(): void {
        LOG.debug('dismiss', 'User dismissed the pre-edit warning');
        this.modalRef.dismiss(false);
    }

    /**
     * Return focus to the control that opened the dialog, when supplied.
     *
     * Guarded so it is a no-op if the caller did not pass an originating element
     * or the element is no longer connected to the document.
     */
    private restoreFocus(): void {
        const target = this.originatingElement;
        if (target && typeof target.focus === 'function' && target.isConnected) {
            target.focus();
        }
    }
}

/**
 * Configuration for {@link EditConfirmationDialogComponent.open}.
 */
export interface ConfirmationConfig {
    /**
     * The control that triggered the dialog (typically the navigator topic
     * button). Focus is returned to it on dismiss (Requirement 2.3).
     */
    originatingElement?: HTMLElement;
}
